import axios from "axios";
import router from "@/router";
import store from "@/store";
import { isNotEmpty } from "@/util";
import { useLoading } from '@/components/composables/useLoading';
const { show, hide } = useLoading();

let API_URL = process.env.VUE_APP_API_URL || '';

// Desenvolvimento: API configurada como localhost e sistema aberto pelo IP da máquina (celular na mesma rede) — usa o
// mesmo host da página, senão o celular procura a API nele mesmo. Em produção a URL não é localhost e nada muda.
try {
    const u = new URL(API_URL);
    const local = ['localhost', '127.0.0.1'];
    if (local.includes(u.hostname) && !local.includes(window.location.hostname)) {
        u.hostname = window.location.hostname;
        API_URL = u.toString().replace(/\/$/, '');
    }
} catch { /* URL relativa ou vazia: mantém */ }

export { API_URL };

//console.log('API_URL', API_URL);

function authHeader() {
    const token = JSON.parse(localStorage.getItem('mizunetoken'));
    if (token) {
        return { Authorization: 'Bearer ' + token };
    } else {
        return {};
    }
}

const api = axios.create({
    baseURL: API_URL
});

api.interceptors.request.use(
    (config) => {
        if (!config.noLoading) {
            show();
        }
        config.headers = {
            ...config.headers,
            ...authHeader()
        };
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        if (!response.config.noLoading) {
            hide();
        }
        const requestPayloadSizeKb = response.config.data ? JSON.stringify(response.config.data).length / 1024 : 0;
        const responseSizeKb = response.data ? JSON.stringify(response.data).length / 1024 : 0;
        const method = response.config.method?.toUpperCase() || 'UNKNOWN';
        const url = response.config.url || '';
        if (requestPayloadSizeKb > 1000) {
            console.warn(`📤 Dados ENVIADOS pesados (${formatSize(requestPayloadSizeKb)}) [${method}] para [${url}]`);
            //console.warn("Payload enviado:", response.config.data);
        }
        if (responseSizeKb > 1000) {
            console.warn(`📥 Dados RECEBIDOS pesados (${formatSize(responseSizeKb)}) [${method}] da resposta de [${url}]`);
            //console.warn("Resposta recebida:", response.data);
        }
        return response;
    },
    (error) => {
        if (!error.config?.noLoading) {
            hide();
        }
        // objeto completo no console (request/response/data) para depuração, em desenvolvimento e no build
        console.error(`[API] ${error.config?.method?.toUpperCase() || ''} ${error.config?.url || ''}`, error.response?.data ?? error.message, error);
        const errorMessage = error.response?.data?.message;
        // semTelaErro: telas que funcionam offline (app do ACS) tratam a falta de rede sozinhas, sem ir para a tela de erro
        if (router && error.code === "ERR_NETWORK" && !error.config?.semTelaErro) {
            router.push({
                name: 'errorPage',
                state: {
                    error: "Erro de Conexão",
                    message: "Não foi possível estabelecer uma conexão com a API.",
                    path: ""
                }
            });
        }
        if (router && errorMessage?.includes("Unable to acquire JDBC Connection [Connection to")) {
            router.push({
                name: 'errorPage',
                state: {
                    error: "Erro de Conexão com o banco de dados",
                    message: "Ocorreu um erro na API que não conseguiu se conectar à base de dados.",
                    path: ""
                }
            });
        }
        if (error.response?.status === 402 && error.response?.data?.codigo === 'LICENCA_VENCIDA') {
            store.commit('limparLicencas');
        }
        if (router && errorMessage === "Token JWT inválido ou expirado!") {
            router.push('/logout');
        }
        return Promise.reject(error);
    }
);


export async function get(url, config = {}) {
    const ret = await api.get(url, config);
    return ret.data;
}

export async function post(url, data, config = {}) {
    const ret = await api.post(url, data, config);
    return ret.data;
}

export async function put(url, data, config = {}) {
    const ret = await api.put(url, data, config);
    return ret.data;
}

export async function remove(url, config = {}) {
    const ret = await api.delete(url, config);
    return ret.data;
}

export async function getDefaultPagination(urlBase, page, size, sortField, sortOrder, filter) {
    let params = '?';
    filter.forEach(filter => {
        if (isNotEmpty(filter.value)) {
            params += `${filter.filter}=${filter.value}&`;
        }
    });
    if (isNotEmpty(sortField)) {
        params += `sort=${sortField},${sortOrder}&`;
    }
    params += `page=${page + 1}&size=${size}`;
    return await get(`${urlBase}${params}`);
}

function formatSize(sizeInKb) {
    return sizeInKb >= 1024
        ? `${(sizeInKb / 1024).toFixed(2)} MB`
        : `${sizeInKb.toFixed(2)} KB`;
}

export default api